import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import classnames from "classnames";
import { useHistory, Link, useParams } from "react-router-dom";
import useSWR, { mutate } from "swr";
import * as Sentry from "@sentry/react";
import request from "axios";
import { useClient } from "../api";
import Spinner from "../spinner/Spinner";
import { adjustDate } from "../../../helpers/date";

const ListBuilder = React.forwardRef(
  ({ onChange, onBlur, value, name, help }, ref) => {
    let cleanValue = value;
    if (!Array.isArray(value)) {
      cleanValue = [];
    }
    const [items, setItems] = useState(cleanValue);

    const deleteRequirement = e => {
      const newItems = items.filter(el => e !== el);
      setItems(newItems);
    };
    const handleKeyPress = e => {
      if (e.key !== "Enter") return;
      if (e.target.value.trim() === "") return;
      const newItems = [...items, e.target.value.trim()];
      setItems(newItems);
      onChange(newItems);
      e.target.value = "";
      e.preventDefault();
    };

    return (
      <div className="tw-w-full tw-mb-4">
        <div className="tw-relative tw-flex tw-flex-col">
          <input
            name={name}
            ref={ref}
            onKeyPress={handleKeyPress}
            onBlur={onBlur}
            className="tw-border tw-pl-2 tw-rounded-md tw-border-gray-300 tw-outline-none tw-text-sm tw-h-10 tw-rounded tw-w-full"
            type="text"
          />
          {help && (
            <span className="tw-block tw-text-sm tw-text-gray-700">{help}</span>
          )}
        </div>
        <div className="tw-pt-8 tw-pl-2 tw-flex tw-w-full tw-flex-wrap tw--mx-3 tw-mb-1">
          {items.length > 0 &&
            items.map((v, i) => (
              <div
                key={i}
                className="tw-flex tw-justify-center tw-items-center tw-m-1 tw-py-1 tw-px-2 tw-rounded-md tw-bg-blue-50 tw-text-base tw-font-medium"
              >
                <div className="tw-flex-initial tw-max-w-full tw-leading-none tw-text-xs tw-font-normal">
                  {v}
                </div>
                <span
                  onClick={() => deleteRequirement(v)}
                  className="tw-ml-2 tw-mb-1 tw-cursor-pointer"
                >
                  x
                </span>
              </div>
            ))}
        </div>
      </div>
    );
  }
);

const DayPicker = ({ value, onChange }) => {
  const [checkedDays, setCheckedDays] = useState(value);
  const days = [
    { value: "MON", name: "Monday" },
    { value: "TUE", name: "Tuesday" },
    { value: "WED", name: "Wednesday" },
    { value: "THU", name: "Thursday" },
    { value: "FRI", name: "Friday" },
    { value: "SAT", name: "Saturday" },
    { value: "SUN", name: "Sunday" }
  ];

  const handleChange = e => {
    let newCheckedDays = [...checkedDays];
    if (checkedDays.includes(e.target.name)) {
      newCheckedDays = checkedDays.filter(v => v !== e.target.name);
    } else {
      newCheckedDays = [...checkedDays, e.target.name];
    }
    setCheckedDays(newCheckedDays);
    onChange(newCheckedDays);
  };

  return (
    <div className="tw-flex tw-pl-2 tw-flex-wrap tw--mx-3">
      {days.map(day => (
        <input
          className={classnames({
            "tw-bg-blue-50 tw-rounded-md tw-pointer tw-text-sm tw-p-1 tw-m-2": !checkedDays.includes(
              day.value
            ), // Not selected
            "tw-bg-orange tw-text-white tw-rounded-md tw-pointer tw-text-sm tw-p-1 tw-m-2": checkedDays.includes(
              day.value
            ) // Selected
          })}
          value={day.name}
          key={day.value}
          name={day.value}
          type="button"
          onClick={handleChange}
        />
      ))}
    </div>
  );
};

export default function CreateBookingForm({ orgEmails, token }) {
  const {
    register,
    control,
    handleSubmit,
    setError,
    getValues,
    setValue
  } = useForm({ shouldUseNativeValidation: true });
  const client = useClient();
  const history = useHistory();

  const urlVacancyId = () => {
    const vId = history.location.search.replace("?", "");
    if (/^[a-z0-9]+$/gi.test(vId)) {
      return vId;
    }
    return "";
  };

  const { vacancyID } = useParams();

  const vacancyId = vacancyID || urlVacancyId();

  let vacancyToEdit;
  let startDateForEdit;
  let endDateForEdit;
  let startTimeEdit;
  let endTimeEdit;

  if (vacancyId) {
    const response = useSWR(`/vacancies/${vacancyId}`);
    vacancyToEdit = response.data;

    if (vacancyToEdit) {
      const convertedStartDate = new Date(vacancyToEdit.startDate);
      startDateForEdit = convertedStartDate.toISOString().substring(0, 10);

      const convertedEndDate = vacancyToEdit.endDate
        ? new Date(vacancyToEdit.endDate)
        : null;
      endDateForEdit = vacancyToEdit.endDate
        ? convertedEndDate.toISOString().substring(0, 10)
        : null;

      const adjustStartTime = adjustDate(vacancyToEdit.workHoursStart);
      const adjustEndTime = adjustDate(vacancyToEdit.workHoursEnd);

      startTimeEdit = format(new Date(adjustStartTime), "HH:mm");
      endTimeEdit = format(new Date(adjustEndTime), "HH:mm");

      setValue("title", vacancyToEdit.title, {});
      setValue("description", vacancyToEdit.description, {});
      setValue("reason", vacancyToEdit.reason, {});
      setValue("workDays", vacancyToEdit.workDays, {});
      setValue("workersRequired", vacancyToEdit.workersRequired, {});
      setValue("location", vacancyToEdit.location, {});
      setValue(
        "qualificationRequirements",
        vacancyToEdit.qualificationRequirements,
        {}
      );
      setValue("payRate", vacancyToEdit.payRate || 0, {});
      setValue("workType", vacancyToEdit.workType, {});
      setValue("payRateFreq", vacancyToEdit.payRateFreq, {});
      setValue("town", vacancyToEdit.town, {});
      setValue("address", vacancyToEdit.address, {});
      setValue("county", vacancyToEdit.county, {});
      setValue("postcode", vacancyToEdit.postcode, {});
      setValue(
        "additionalInformation",
        vacancyToEdit.additionalInformation,
        {}
      );
      setValue("approverEmail", vacancyToEdit.approverEmail, {});
      setValue("startDate", startDateForEdit, {});
      setValue("endDate", endDateForEdit, {});
      setValue("workHoursStart", startTimeEdit, {});
      setValue("workHoursEnd", endTimeEdit, {});
    }
    console.log("response2 --------> ", response.data);
    vacancyToEdit = response.data;
  }

  if (!vacancyToEdit && vacancyId) {
    return <Spinner />;
  }

  const onSubmit = async data => {
    try {
      const updatedData = data;
      // updatedData.endDate = dateParse(data.endDate, "yyyy-MM-dd", new Date());
      // updatedData.startDate = dateParse(
      //   data.startDate,
      //   "yyyy-MM-dd",
      //   new Date()
      // );
      console.log("workHoursStart");
      console.log(data.workHoursStart);

      // dateParse(
      //   data.workHoursStart,
      //   "HH:mm",
      //   new Date()
      // );

      const [startHour, startMin] = data.workHoursStart.split(/:/);
      let newStartHours = new Date();
      const userTimezoneOffset = newStartHours.getTimezoneOffset() * 60000;

      newStartHours.setHours(startHour);
      newStartHours.setMinutes(startMin);
      newStartHours.setSeconds("00");
      newStartHours.setMilliseconds("00");
      newStartHours = new Date(newStartHours.getTime() - userTimezoneOffset);

      const [endHour, endMin] = data.workHoursEnd.split(/:/);
      let newEndHours = new Date();

      newEndHours.setHours(endHour);
      newEndHours.setMinutes(endMin);
      newEndHours.setSeconds("00");
      newEndHours.setMilliseconds("00");
      newEndHours = new Date(newEndHours.getTime() - userTimezoneOffset);

      updatedData.workHoursStart = newStartHours;

      updatedData.workHoursEnd = newEndHours;

      const { data: vacancy } = vacancyID // we use the params ID here for patching as this comes from the edit route
        ? await client.patch(`/vacancies/${vacancyID}`, updatedData)
        : await client.put("/vacancies", updatedData); // for new or templated vacancies

      if (!vacancyId) {
        // only send approve email on new vacancy + including email to national sales team

        const emailValues = {
          creatorEmail: vacancy.creatorEmail,
          vacancyTitle: vacancy.title,
          vacancyLocation: vacancy.location
            ? vacancy.location
            : `${vacancy.address}, ${vacancy.town}, ${vacancy.county}, ${vacancy.postcode}`
        };

        await request.post(
          // send an email to the national hrgo team @ (rob.elliott@hrgo.co.uk)
          `${process.env.REACT_APP_PROFILE_API_URL}/clientDashboard/sendVacancyNationalHRGOTeam`,
          {
            emailAddress: "rob.elliott@hrgo.co.uk",
            vacancy: emailValues
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      }

      // Use mutate to update SWR cache so page change is instant
      mutate(`/vacancies${vacancy.vacancyId}`, updatedData);
      history.push(`/vacancies`);
    } catch (error) {
      if (error.response && error.response.data && error.response.data.issues) {
        error.response.data.issues.map(({ path, message }) =>
          setError(path[0], { type: "manual", message }, { shouldFocus: true })
        );
      } else {
        Sentry.captureException(error, {
          form: { values: getValues() }
        });
      }
    }
  };

  const FormField = ({ children }) => (
    <div className="md:tw-w-1/4 tw-w-1/2 tw-px-3 tw-flex tw-flex-col tw-mb-6 ">
      {children}
    </div>
  );

  const rateOptions = ["per hour", "per day", "per week", "per annum"];
  const workTypeOptions = ["Temporary", "Permanent", "Contract"];

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="lg:tw-px-32  tw-py-16">
          <div className="md:tw-p-4 tw-p-2 tw-rounded-md tw-bg-white tw-flex tw-flex-col">
            <div className="tw-p-4 tw-rounded-md tw-bg-white tw-flex tw-flex-col tw-rounded tw-overflow-hidden tw-text-center">
              <h1 className="tw-p-2 tw-ml-2 tw-text-lg tw-font-bold tw-font-normal tw-text-gray-500">
                New Vacancy
              </h1>
            </div>
            <div className="tw-flex tw-flex-wrap tw--mx-3 tw-mb-1">
              <FormField>
                <label
                  className="tw-text-gray-700 tw-text-xs tw-mb-2"
                  htmlFor="title"
                >
                  Title
                </label>
                <input
                  required
                  {...register("title", {})}
                  className="tw-border tw-pl-2 tw-rounded-md tw-border-gray-300 tw-outline-none tw-text-sm tw-h-10 tw-z-0 "
                  type="text"
                />
              </FormField>
              <FormField>
                <label
                  className="tw-text-gray-700 tw-text-xs tw-mb-2"
                  htmlFor="reason"
                >
                  Reason for New Vacancy
                </label>
                <div className="tw-relative">
                  <select
                    {...register("reason", {})}
                    className="tw-border tw-w-full tw-pl-2 tw-rounded-md tw-border-gray-300 tw-outline-none tw-text-sm tw-h-10  tw-rounded tw-z-0 tw-focus:shadow tw-focus:outline-none"
                  >
                    <option value="">Please select reason...</option>
                    <option>Sick</option>
                    <option>Holiday</option>
                    <option>Extra work</option>
                    <option>Contract Mobilisation</option>
                    <option>Temp to Perm</option>
                    <option>Parental Leave</option>
                    <option>Vacant Role</option>
                    <option>Replacement</option>
                    <option>Other</option>
                  </select>
                </div>
              </FormField>
              <FormField>
                <label
                  className="tw-text-gray-700 tw-text-xs tw-mb-2"
                  htmlFor="workDays"
                >
                  Work Type
                </label>
                <div className="tw-relative">
                  <select
                    required
                    {...register("workType")}
                    className="tw-border tw-rounded-md tw-w-full tw-border-gray-300 tw-outline-none tw-text-sm tw-h-10  tw-rounded tw-z-0 tw-focus:shadow tw-focus:outline-none"
                  >
                    {workTypeOptions.map(workType => (
                      <option key={workType}>{workType}</option>
                    ))}
                  </select>
                </div>
              </FormField>
              <FormField>
                <label
                  className="tw-text-gray-700 tw-text-xs tw-mb-2"
                  htmlFor="workersRequired"
                >
                  Number of Workers Required
                </label>
                <div className="tw-relative">
                  <input
                    pattern="[0-9]*"
                    type="number"
                    {...register("workersRequired", {
                      min: 0,
                      valueAsNumber: true
                    })}
                    className="tw-border tw-w-full tw-pl-2 tw-rounded-md tw-border-gray-300 tw-outline-none tw-text-sm tw-h-10  tw-rounded tw-z-0 tw-focus:shadow tw-focus:outline-none"
                  />
                </div>
              </FormField>
            </div>
            <div className="tw-flex tw-flex-wrap tw--mx-3 tw-mb-1">
              <FormField>
                <label
                  className="tw-text-gray-700 tw-text-xs tw-mb-2"
                  htmlFor="grid-first-name"
                >
                  Address Line 1
                </label>
                <input
                  required
                  {...register("address", {})}
                  className="tw-border tw-pl-2 tw-rounded-md tw-border-gray-300 tw-outline-none tw-text-sm tw-h-10 tw-z-0 "
                  type="text"
                />
              </FormField>
              <FormField>
                <label
                  className="tw-text-gray-700 tw-text-xs tw-mb-2"
                  htmlFor="grid-first-name"
                >
                  Town
                </label>
                <input
                  required
                  {...register("town", {})}
                  className="tw-border tw-pl-2 tw-rounded-md tw-border-gray-300 tw-outline-none tw-text-sm tw-h-10 tw-z-0 "
                  type="text"
                />
              </FormField>
              <FormField>
                <label
                  className="tw-text-gray-700 tw-text-xs tw-mb-2"
                  htmlFor="grid-first-name"
                >
                  County
                </label>
                <input
                  required
                  {...register("county", {})}
                  className="tw-border tw-pl-2 tw-rounded-md tw-border-gray-300 tw-outline-none tw-text-sm tw-h-10 tw-z-0 "
                  type="text"
                />
              </FormField>
              <FormField>
                <label
                  className="tw-text-gray-700 tw-text-xs tw-mb-2"
                  htmlFor="grid-first-name"
                >
                  Postcode
                </label>
                <input
                  required
                  {...register("postcode", {})}
                  className="tw-border tw-pl-2 tw-rounded-md tw-border-gray-300 tw-outline-none tw-text-sm tw-h-10 tw-z-0 "
                  type="text"
                  pattern="[a-zA-Z0-9\s]+"
                />
              </FormField>
            </div>
            <div className="tw-flex tw-flex-wrap tw--mx-3 tw-mb-1">
              <FormField>
                <label
                  className="tw-text-gray-700 tw-text-xs tw-mb-2"
                  htmlFor="grid-start-date"
                >
                  Start Date
                </label>
                <input
                  required
                  type="date"
                  className="tw-border tw-pl-1 tw-rounded-md tw-border-gray-300 tw-outline-none tw-text-sm tw-h-10 "
                  placeholder="Start"
                  {...register("startDate", {
                    valueAsDate: true
                    // setValueAs: v => dateParse(v, "yyyy-MM-dd", new Date())
                  })}
                />
              </FormField>
              <FormField>
                <label
                  className="tw-text-gray-700 tw-text-xs tw-mb-2"
                  htmlFor="endDate"
                >
                  End Date
                </label>
                <input
                  defaultValue={endDateForEdit}
                  type="date"
                  className="tw-border tw-pl-1 tw-rounded-md tw-border-gray-300 tw-outline-none tw-text-sm tw-h-10 "
                  placeholder="End"
                  {...register("endDate", {
                    valueAsDate: true
                    // setValueAs: v => dateParse(v, "yyyy-MM-dd", new Date())
                  })}
                />
                <label className="tw-text-gray-700 tw-text-xs tw-mb-2">
                  (if unknown, leave blank)
                </label>
              </FormField>
              <FormField>
                <label
                  className="tw-text-gray-700 tw-text-xs tw-mb-2"
                  htmlFor="workHoursStart"
                >
                  Start Time:
                </label>
                <input
                  required
                  defaultValue={startTimeEdit}
                  className="tw-border tw-w-full tw-pl-2 tw-rounded-md tw-border-gray-300 w-outline-none tw-text-sm tw-h-10 "
                  type="time"
                  {...register("workHoursStart", {
                    // setValueAs: v => dateParse(v, "HH:mm", new Date())
                  })}
                />
              </FormField>

              <FormField>
                <label
                  className="tw-text-gray-700 tw-text-xs tw-mb-2"
                  htmlFor="workHoursEnd"
                >
                  End Time:
                </label>
                <div className="tw-relative">
                  <input
                    required
                    defaultValue={endTimeEdit}
                    className="tw-border tw-w-full tw-pl-2 tw-rounded-md tw-border-gray-300 tw-outline-none tw-text-sm tw-h-10 "
                    type="time"
                    {...register("workHoursEnd", {
                      // setValueAs: v => dateParse(v, "HH:mm", new Date())
                    })}
                  />
                </div>
              </FormField>
            </div>
            <div className="tw-flex tw-flex-col md:tw-flex-row tw--mx-3 tw-mb-1">
              <div className="tw-w-full md:tw-w-2/3  tw-px-3 tw-mb-6 ">
                <label
                  className="tw-text-gray-700 tw-text-xs tw-mb-2"
                  htmlFor="workDays"
                >
                  Working Days:
                </label>
                <div className="tw-relative">
                  <Controller
                    control={control}
                    name="workDays"
                    defaultValue={[]}
                    render={({ field }) => (
                      <DayPicker
                        onChange={field.onChange}
                        value={field.value}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
            <div className="tw-flex tw-flex-wrap tw--mx-3 tw-mb-1">
              <div className="flex-grow-1 tw-px-3 tw-flex tw-flex-col tw-mb-6 ">
                <label
                  className="tw-text-gray-700 tw-text-xs tw-mb-2"
                  htmlFor="description"
                >
                  Description
                </label>
                <textarea
                  required
                  {...register("description", {})}
                  className="tw-border tw-pl-2 tw-rounded-md tw-border-gray-300 tw-outline-none tw-text-sm tw-h-10 tw-z-0"
                />
              </div>
            </div>
            <div className="tw-flex tw-flex-wrap tw--mx-3 tw-mb-1">
              <div className="flex-grow-1 tw-px-3 tw-flex tw-flex-col tw-mb-6 ">
                <label
                  className="tw-text-gray-700 tw-text-xs tw-mb-2"
                  htmlFor="additionalInformation"
                >
                  Additional Information
                </label>
                <textarea
                  placeholder="e.g. Health & Safety, uniform, transport"
                  {...register("additionalInformation", {})}
                  className="tw-border tw-pl-2 tw-rounded-md tw-border-gray-300 tw-outline-none tw-text-sm tw-h-10 tw-z-0"
                />
              </div>
            </div>
            <div className="tw-flex tw-flex-wrap tw--mx-3 tw-mb-1">
              <FormField>
                <label
                  className="tw-text-gray-700 tw-text-xs tw-mb-2"
                  htmlFor="payRate"
                >
                  Pay Rate
                </label>
                <div className="tw-relative tw-flex">
                  <input
                    type="text"
                    {...register("payRate", {
                      min: 0,
                      valueAsNumber: true
                    })}
                    className="tw-border tw-pl-2 tw-rounded-md tw-border-gray-300 tw-outline-none tw-text-sm tw-h-10  tw-rounded tw-z-0 tw-focus:shadow tw-focus:outline-none"
                  />
                  <select
                    required
                    {...register("payRateFreq")}
                    className="tw-border tw-pl-2 tw-ml-2 tw-rounded-md tw-border-gray-300 tw-outline-none tw-text-sm tw-h-10  tw-rounded tw-z-0 tw-focus:shadow tw-focus:outline-none"
                  >
                    {rateOptions.map(rateOption => (
                      <option key={rateOption}>{rateOption}</option>
                    ))}
                  </select>
                </div>
              </FormField>
            </div>

            <div className="tw-w-full tw-w-flex tw-mb-6 ">
              <label
                className="tw-text-gray-700 tw-text-xs tw-mb-2"
                htmlFor="qualificationRequirements"
              >
                Skills required
              </label>
              <div className="tw-relative">
                <Controller
                  control={control}
                  name="qualificationRequirements"
                  defaultValue={[]}
                  render={({ field }) => (
                    <ListBuilder
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                      ref={field.ref}
                      value={field.value}
                      name={field.name}
                      help={`(press enter key after each skill is entered)`}
                    />
                  )}
                />
              </div>
            </div>
            <div className="tw-hidden tw-flex tw-full tw--mx-3 tw-mb-1">
              <div className="tw-w-full tw-flex tw-flex-col tw-px-3 tw-mb-6">
                <label
                  className="tw-text-gray-700 tw-text-xs tw-mb-2"
                  htmlFor="approverEmail"
                >
                  Approver
                </label>
                <select
                  required
                  {...register("approverEmail")}
                  className="tw-border md:tw-w-auto tw-w-full tw-pl-2 tw-rounded-md tw-border-gray-300 tw-outline-none tw-text-sm tw-h-10  tw-rounded tw-z-0 tw-focus:shadow tw-focus:outline-none"
                >
                  <option value="">Select approver...</option>
                  {orgEmails.map(email => (
                    <option key={email}>{email}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="tw-flex tw-items-center tw-justify-between">
              <Link
                className="tw-bg-gray-300 tw-hover:bg-gray-400 tw-text-gray-800 tw-font-bold tw-py-2 tw-px-4 tw-rounded tw-inline-flex tw-items-center"
                to={"/vacancies"}
              >
                Back to overview
              </Link>
              <button
                type="submit"
                className="btn btn-listItem btn-primary btn-md"
              >
                Submit Vacancy
              </button>
            </div>
          </div>
        </div>
      </form>
    </>
  );
}
